/**
 * Client-side mapping for the database's usage-limit enforcement errors.
 *
 * Enforcement triggers raise `limit_exceeded:<meter>`; that marker survives
 * any wrapping ("Failed to create campaign: ... limit_exceeded:campaigns"),
 * so these helpers search the message for the marker and translate it into
 * an upgrade-oriented message for the user.
 */

export type UsageMeter =
  | 'influencers'
  | 'campaigns'
  | 'team_members'
  | 'deliverables'
  | 'profile_refreshes';

const LIMIT_ERROR_PREFIX = 'limit_exceeded:';

const LIMIT_MESSAGES: Record<UsageMeter, string> = {
  influencers:
    "You've reached your plan's influencer limit. Upgrade your plan in Settings to add more influencers.",
  campaigns:
    "You've reached your plan's campaign limit. Upgrade your plan in Settings to create more campaigns.",
  team_members:
    "You've reached your plan's team member limit. Upgrade your plan in Settings to add more seats.",
  deliverables:
    "You've reached this month's deliverable limit. Upgrade your plan in Settings to add or refresh more deliverables.",
  profile_refreshes:
    "You've reached this month's profile refresh limit. Upgrade your plan in Settings to refresh more profiles.",
};

const isUsageMeter = (value: string): value is UsageMeter =>
  value in LIMIT_MESSAGES;

const extractMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return '';
};

/** Returns the exceeded meter if the error is a usage-limit rejection. */
export const getLimitExceededMeter = (error: unknown): UsageMeter | null => {
  const message = extractMessage(error);
  const index = message.indexOf(LIMIT_ERROR_PREFIX);
  if (index === -1) return null;

  const meter = message
    .slice(index + LIMIT_ERROR_PREFIX.length)
    .match(/^[a-z_]+/)?.[0];

  return meter && isUsageMeter(meter) ? meter : null;
};

const LIFECYCLE_MESSAGES: Record<string, string> = {
  trial_expired:
    "Your 14-day free trial has expired. Upgrade your plan in Settings to continue adding records.",
  past_due_restricted:
    "Your payment grace period has ended. Please update your billing details in Settings to continue.",
  subscription_inactive:
    "Your organization's subscription is inactive. Please upgrade or contact support.",
  subscription_canceled:
    "Your subscription has been canceled. Please reactivate your account in Settings.",
  subscription_missing:
    "No active subscription found. Please contact support to provision your account.",
};

export const limitExceededMessage = (meter: UsageMeter): string =>
  LIMIT_MESSAGES[meter];

/**
 * Returns the friendly upgrade message when the error is a limit rejection
 * or subscription lifecycle restriction, otherwise the provided fallback.
 */
export const toFriendlyLimitError = (
  error: unknown,
  fallback: string
): string => {
  const message = extractMessage(error);
  for (const [code, friendly] of Object.entries(LIFECYCLE_MESSAGES)) {
    if (message.includes(code)) return friendly;
  }
  const meter = getLimitExceededMeter(error);
  return meter ? LIMIT_MESSAGES[meter] : fallback;
};
